import { addMonths, setDayOfMonth } from '@pesto/utils';
import { addDays, isValid, parseISO } from 'date-fns';

import type { Loan, Program, Statement } from '../__generated__/graphql/api';

export type StatementScheduleConfig = Pick<Loan, 'statementDay' | 'paymentDueDay'> &
  Pick<Program, 'lateFeeGracePeriodDays'>;

export const hasStatementSchedule = (
  config: StatementScheduleConfig | null | undefined,
): config is StatementScheduleConfig & { statementDay: number; paymentDueDay: number } =>
  !!config &&
  config.statementDay != null &&
  Number.isInteger(config.statementDay) &&
  config.statementDay >= 1 &&
  config.statementDay <= 28 &&
  config.paymentDueDay != null &&
  Number.isInteger(config.paymentDueDay) &&
  config.paymentDueDay >= 1 &&
  config.paymentDueDay <= 28 &&
  config.statementDay !== config.paymentDueDay;

export const getStatementPeriodEndError = (
  periodTo: string,
  config: StatementScheduleConfig | null | undefined,
): string | undefined => {
  if (!hasStatementSchedule(config)) return 'This loan has no internally managed billing schedule.';
  const end = parseISO(periodTo);

  if (!isValid(end)) return 'Enter a valid period end date.';

  if (addDays(end, 1).getDate() !== config.statementDay) {
    return `The period must end immediately before generation day ${config.statementDay}. The submitted date will not be adjusted.`;
  }

  return undefined;
};

export interface StatementScheduleCycle {
  generateDate: Date;
  dueDate: Date;
  dueDateProcessingDate: Date;
  lateFeeDate?: Date;
  periodFrom: Date;
  periodTo: Date;
}

const APPROX_PROCESSING_HOUR = 4;

const withApproxProcessingTime = (date: Date): Date => {
  const next = new Date(date);
  next.setHours(APPROX_PROCESSING_HOUR, 0, 0, 0);

  return next;
};

export const formatStatementCalendarDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const getStatementProcessingDates = ({
  dueDate,
  lateFeeEligibleOn,
}: {
  dueDate: Date;
  lateFeeEligibleOn?: Date;
}): Pick<StatementScheduleCycle, 'dueDate' | 'dueDateProcessingDate' | 'lateFeeDate'> => {
  const dueDateProcessingDate = addDays(dueDate, 1);

  const lateFeeDate = lateFeeEligibleOn
    ? new Date(Math.max(lateFeeEligibleOn.getTime(), dueDateProcessingDate.getTime()))
    : undefined;

  return { dueDate, dueDateProcessingDate, lateFeeDate };
};

export const deriveStatementScheduleDates = ({
  periodTo,
  paymentDueDay,
  lateFeeGracePeriodDays,
}: {
  periodTo: Date;
  paymentDueDay: number;
  lateFeeGracePeriodDays?: number | null;
}): Pick<StatementScheduleCycle, 'dueDate' | 'dueDateProcessingDate' | 'lateFeeDate'> => {
  const generationDate = addDays(periodTo, 1);
  const candidate = setDayOfMonth(generationDate, paymentDueDay);
  const dueDate = candidate > generationDate ? candidate : addMonths(candidate, 1);

  const lateFeeEligibleOn =
    lateFeeGracePeriodDays != null && lateFeeGracePeriodDays >= 0
      ? addDays(dueDate, lateFeeGracePeriodDays + 1)
      : undefined;

  return getStatementProcessingDates({ dueDate, lateFeeEligibleOn });
};

export const getStatementCycleForMonth = (
  monthDate: Date,
  config: StatementScheduleConfig,
): StatementScheduleCycle | null => {
  if (!hasStatementSchedule(config)) return null;

  const generateDate = withApproxProcessingTime(setDayOfMonth(monthDate, config.statementDay));

  const periodTo = addDays(generateDate, -1);
  const periodFrom = setDayOfMonth(addMonths(generateDate, -1), config.statementDay);

  const { dueDate, dueDateProcessingDate, lateFeeDate } = deriveStatementScheduleDates({
    periodTo,
    paymentDueDay: config.paymentDueDay,
    lateFeeGracePeriodDays: config.lateFeeGracePeriodDays,
  });

  return {
    generateDate,
    dueDate,
    dueDateProcessingDate,
    lateFeeDate,
    periodFrom,
    periodTo,
  };
};

export const getStatementCyclesInRange = (
  startDate: Date,
  endDate: Date,
  config: StatementScheduleConfig,
): StatementScheduleCycle[] => {
  const cycles: StatementScheduleCycle[] = [];

  let currentDate = new Date(startDate);
  currentDate.setDate(1);

  const extendedStart = addMonths(currentDate, -1);
  const extendedEnd = addMonths(endDate, 2);

  currentDate = new Date(extendedStart);

  while (currentDate <= extendedEnd) {
    const cycle = getStatementCycleForMonth(currentDate, config);

    if (cycle && cycle.generateDate >= startDate && cycle.generateDate <= endDate) {
      cycles.push(cycle);
    }

    currentDate = addMonths(currentDate, 1);
  }

  return cycles;
};

export const getLatestStatementCycle = (asOf: Date, config: StatementScheduleConfig): StatementScheduleCycle | null => {
  const cycle = getStatementCycleForMonth(asOf, config);

  if (!cycle) return null;

  return formatStatementCalendarDate(cycle.generateDate) <= formatStatementCalendarDate(asOf)
    ? cycle
    : getStatementCycleForMonth(addMonths(asOf, -1), config);
};

export const getDefaultStatementPeriod = ({
  asOf,
  schedule,
  statements,
}: {
  asOf: Date;
  schedule: StatementScheduleConfig;
  statements: Array<Pick<Statement, 'billingPeriodEnd'>>;
}): { from: string; to: string } | null => {
  const latestEnd = statements.reduce(
    (latest, statement) => (statement.billingPeriodEnd > latest ? statement.billingPeriodEnd : latest),
    '',
  );

  if (!latestEnd) {
    const cycle = getLatestStatementCycle(asOf, schedule);

    return cycle
      ? { from: formatStatementCalendarDate(cycle.periodFrom), to: formatStatementCalendarDate(cycle.periodTo) }
      : null;
  }

  const from = addDays(parseISO(latestEnd), 1);
  let cycle = getStatementCycleForMonth(from, schedule);

  if (cycle && formatStatementCalendarDate(cycle.periodTo) <= latestEnd) {
    cycle = getStatementCycleForMonth(addMonths(from, 1), schedule);
  }

  if (!cycle || formatStatementCalendarDate(cycle.generateDate) > formatStatementCalendarDate(asOf)) return null;

  return { from: formatStatementCalendarDate(from), to: formatStatementCalendarDate(cycle.periodTo) };
};
